import React from 'react';
import { useSimpleVoiceChat } from '../../hooks/useSimpleVoiceChat';
import { Button } from '@/components/ui/button';
import { Mic, MicOff, Volume2, Loader2 } from 'lucide-react';

interface SimpleVoiceChatProps {
  positId: string;
  className?: string;
}

export const SimpleVoiceChat: React.FC<SimpleVoiceChatProps> = ({ 
  positId, 
  className = '' 
}) => {
  const {
    isListening,
    isProcessing,
    isSpeaking,
    currentTranscript,
    lastResponse,
    startListening,
    stopListening,
    error
  } = useSimpleVoiceChat(positId);

  const handleToggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const getStatusText = () => {
    if (error) return 'Error occurred';
    if (isProcessing) return 'Processing...';
    if (isSpeaking) return 'AI Speaking';
    if (isListening) return 'Listening...';
    return 'Ready to listen';
  };

  const getStatusColor = () => {
    if (error) return 'text-red-600';
    if (isProcessing) return 'text-blue-600';
    if (isSpeaking) return 'text-green-600';
    if (isListening) return 'text-orange-600';
    return 'text-gray-600';
  };

  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Voice Chat</h3>
        <div className="text-sm text-gray-500">OpenAI TTS + Speech Recognition</div>
      </div>

      {/* Status */}
      <div className="text-center mb-6">
        <div className={`text-sm font-medium ${getStatusColor()}`}>
          {getStatusText()}
        </div>
        
        {/* Visual indicator */}
        <div className="mt-3 flex justify-center">
          <div className={`w-16 h-16 rounded-full border-4 flex items-center justify-center transition-all ${
            error 
              ? 'border-red-400 bg-red-50' 
              : isProcessing
                ? 'border-blue-400 bg-blue-50 animate-pulse'
                : isSpeaking 
                  ? 'border-green-400 bg-green-50 animate-pulse' 
                  : isListening
                    ? 'border-orange-400 bg-orange-50 animate-pulse'
                    : 'border-gray-300 bg-gray-50'
          }`}>
            {isProcessing ? (
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            ) : isSpeaking ? (
              <Volume2 className="h-6 w-6 text-green-600" />
            ) : isListening ? (
              <Mic className="h-6 w-6 text-orange-600" />
            ) : (
              <MicOff className="h-6 w-6 text-gray-400" />
            )}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="text-center mb-4">
        <Button
          onClick={handleToggleListening}
          disabled={isProcessing}
          className={`px-6 py-3 rounded-full font-medium transition-all ${
            isListening 
              ? 'bg-red-600 hover:bg-red-700 text-white' 
              : 'bg-primary hover:bg-primary/90 text-primary-foreground'
          }`}
        >
          {isListening ? (
            <>
              <MicOff className="h-4 w-4 mr-2" />
              Stop Listening
            </>
          ) : (
            <>
              <Mic className="h-4 w-4 mr-2" />
              Start Listening
            </>
          )}
        </Button>
      </div>

      {/* Current transcript */}
      {currentTranscript && (
        <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="text-sm font-medium text-orange-800 mb-1">You said:</div>
          <div className="text-orange-700">{currentTranscript}</div>
        </div>
      )}

      {/* Last response */}
      {lastResponse && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="text-sm font-medium text-blue-800 mb-1">AI Response:</div>
          <div className="text-blue-700">{lastResponse}</div>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="text-sm font-medium text-red-800 mb-1">Error:</div>
          <div className="text-red-700 text-sm">{error}</div>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-6 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="text-sm text-gray-700">
          <strong>How to use:</strong>
          <ul className="mt-2 space-y-1 list-disc list-inside">
            <li>Click "Start Listening" to begin</li>
            <li>Speak clearly into your microphone</li>
            <li>The AI will respond automatically</li>
            <li>Much more reliable than ElevenLabs!</li>
          </ul>
        </div>
      </div>
    </div>
  );
};